<template>
  <div
    class="inspection"
    :class="{ 'card m-b-md': !isModal, 'modal-card': isModal }"
  >
    <header
      :class="{ 'card-header is-dark': !isModal, 'modal-card-head': isModal }"
    >
      <p :class="{ 'card-header-title': !isModal, 'modal-card-title': isModal }">
        <router-link
          v-if="!/Passed|Pending/gi.test(inspection.status) || isAdmin"
          class="is-text is-light"
          :title="`Update ${inspection.name}`"
          :aria-title="`Update ${inspection.name}`"
          :to="editLink()"
        >
          <i
            class="fas fa-edit"
            aria-hidden="true"
          />
          <span class="p-l-xs">{{ inspection.name }}</span>
        </router-link>
        <span v-else>
          {{ inspection.name }}
        </span>
      </p>
      <button
        v-if="isModal"
        class="delete button-delete"
        aria-label="close"
        @click="$emit('close');"
      />
    </header>
    <section :class="{ 'card-content': !isModal, 'modal-card-body': isModal }">
      <div class="p-md content">
        <div class="columns is-multiline m-b-none">
          <!-- <div class="column is-6"> -->
          <p class="column is-6 m-none">
            <strong>Inspection type:</strong>
            <inspection-type :type="inspection.inspectionType" />
          </p>
          <p class="column is-6 m-none">
            <strong>Sample date:</strong>
            {{ $dayjs(inspection.inspectionDate).format('MM/DD/YYYY') }}
          </p>
          <p class="column is-6 m-none">
            <strong>Risk Assessor, Lead Inspector PA Lic. # or EPA Firm Certification #:</strong>
            {{ inspection.licenseOrTechnicianNumber }}
          </p>
          <p class="column is-6 m-none">
            <strong>Laboratory name:</strong>
            {{ inspection.labAccreditationName }}
          </p>
          <p class="column is-6 m-none">
            <strong>Lab Accreditation Number:</strong>
            {{ inspection.labAccreditationNumber }}
          </p>
          <p class="column is-6 m-none">
            <strong>Status:</strong>
            <status-tag :status="inspection.status" />
          </p>
          <p class="column is-6 m-none">
            <strong>Report:</strong>
            <a
              class="link-file"
              target="_blank"
              download
              :href="inspection.reportDownloadUrl"
            >
              <span class="icon is-medium">
                <i
                  class="fad fa-download download-report"
                  aria-hidden="true"
                />
              </span>
              <span>{{ inspection.report }}</span>
            </a>
          </p>
          <p
            v-if="inspection.certificates.length"
            class="column is-6 m-none"
          >
            <strong>Certificate:</strong>
            <a
              v-for="(certificate, index) in inspection.certificates"
              :key="index"  
              class="link-file"
              target="_blank"
              download
              :href="certificate.certificateDownloadUrl"
            >
              <span class="icon is-medium">
                <i
                  class="fad fa-download download-certificate"
                  aria-hidden="true"
                />
              </span>
              <span>{{ certificate.certificateAwsFileName }}</span>
            </a>

            <!-- Hiding submit new certificate for now, still waiting for API. -->
            <a
              v-show="!showForm && /Passed|Pending/gi.test(inspection.status)"
              class="add-new-certificate button is-secondary m-t-sm"
              @click.prevent="showForm = !showForm"
            >
              <span class="icon is-medium">
                <i
                  class="fas fa-file-plus attach-certificate"
                  aria-hidden="true"
                />
              </span>
              <span>Attach new certificate</span>
            </a>
            <validation-observer
              v-if="showForm && /Passed|Pending/gi.test(inspection.status)"
              ref="submit-certificate"
              tag="div"
              class="p-t-md box"
            >
              <h3 class="">
                Upload file
              </h3>
              <div class="field">
                <uploader
                  ref="upload-certificate"
                  v-model="certificateAwsFileName"
                  :url="uploadCertificateURL"
                  :request-parameters-controller="requestCertificateController"
                  :response-parameters-controller="responseCertificateController"
                  class="m-b-xs"
                  @reset="certificateFile = null"
                />
                <div class="control validate-tooltip">
                  <validation-provider
                    v-slot="{ errors, reset }"
                    :rules="{ required: true, ext: [ 'pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx' ] }"
                    name="Certificate file"
                    :vid="`Certificate-file`"
                    mode="eager"
                  >
                    <file
                      v-show="!certificateAwsFileName"
                      v-model="certificateFile"
                      name="certificateFile"
                      class="simple"
                    >
                      <p><small>Extensions allowed: pdf,jpg,jpeg,png,doc,docx</small></p>
                    </file>
                    <transition name="fade">
                      <span
                        v-if="errors[0]"
                        class="cell shrink error popover"
                        tabindex="0"
                        @click="reset()"
                        v-text="errors[0]"
                      />
                    </transition>
                  </validation-provider>
                </div>
              </div>
              <div class="field is-grouped is-grouped-right">
                <div class="control">
                  <button
                    class="button is-small is-secondary cancel-upload"
                    :class="{ 'is-loading': cantCancel }"
                    :disabled="cantCancel"
                    @click.prevent="cancelUpload"
                  >
                    <span>Cancel</span>
                    <span class="icon is-small">
                      <i
                        class="fas fa-times-circle"
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </div>
                <div class="control">
                  <button
                    :disabled="uploading"
                    :class="{ 'is-loading': uploading }"
                    class="button is-small submit-certificate"
                    @click="submitFile"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </validation-observer>
          </p>
          <!-- </div> -->
        </div>
        <h3
          v-show="inspection.dustWipeFigures.length"
          class=""
        >
          Dust wipe samples
        </h3>
        <table class="dustwipe-figures table is-bordered is-striped is-narrow is-hoverable is-fullwidth">
          <tbody>
            <tr
              v-for="(figure, index) in inspection.dustWipeFigures"
              :key="index"
            >
              <td>
                <span v-if="figure.type === 'control'">
                  Control sample
                </span>
                <span v-else>
                  {{ getFigureTitle(figure) }}
                </span>
              </td>
              <td>
                {{ figure.value }}
                µg/ft
                <sup>2</sup>
              </td>
            </tr>
          </tbody>
        </table>

        <template v-if="inspectionStatusHistory.length > 0">
          <hr>
          <h3 class="">
            Status Updates:
          </h3>
          <div
            v-for="(sh, index) in inspectionStatusHistory"
            :key="index"
            class="notification is-info is-light status-update"
          >
            <span class="tag is-info is-light date">{{ $dayjs(sh.statusSetOn).format('MM/DD/YYYY h:mm A') }}</span>
            <strong data-hj-suppress>{{ sh.updatedBy }}</strong> updated the status to <status-tag :status="sh.status" />
            <br>
            <i
              v-if="sh.statusReason"
              v-text="sh.statusReason"
            />
            <br v-if="sh.statusReason">
            <i
              v-if="sh.statusComment"
              v-text="sh.statusComment"
            />
          </div>
        </template>
      </div>
    </section>
    <div
      v-if="isAdmin"
      :class="{ 'has-background-white': updateInspection }"
      class="modal-card-foot"
    >
      <div
        v-show="!updateInspection"
        class="is-fullwidth has-text-centered"
      >
        <button
          v-show="!updateInspection"
          class="is-secondary button changestatus-button"
          @click.prevent="updateInspection = true"
        >
          Change status
        </button>
      </div>
      <validation-observer
        v-show="updateInspection"
        ref="update-status"
        class="is-fullwidth"
        @submit.prevent="false"
      >
        <div class="p-md">
          <div
            class="field"
            :class="{ 'is-grouped': status && status !== 'Passed' }"
          >
            <div class="control is-expanded">
              <dropdown-with-validation
                v-model="status"
                rules="required"
                name="status"
                placeholder="Status"
                required
                default-option="Change status"
                :options="statusOptions"
              />
            </div>
            <div
              v-if="status && status !== 'Passed'"
              class="control is-expanded"
            >
              <dropdown-with-validation
                v-model="statusReason"
                rules="required"
                name="reason"
                placeholder="Reason"
                required
                default-option="Select a reason"
                :options="reasons[status]"
              />
            </div>
          </div>
          <div class="field">
            <div class="control">
              <text-area
                v-model="statusComment"
                rows="2" 
                placeholder="Enter a reason to change the status"
                name="ReasonFailureText"
              />
            </div>
          </div>
          <!-- <div
            v-if="status === 'Failed' || status === 'Revoked'"
            class="content has-background-ghost-grey p-md"
          >
            <h4 class="m-b-none">
              Warning / NOV
            </h4>
            <p>You can choose if you want to issue a warning (future NOV) to the user. Also select the grace time (30 days by default)</p>
            <div
              class="field"
              :class="{ 'is-grouped': warningNOV }"
            >
              <div class="control">
                <checkbox
                  v-model="warningNOV"
                  :value="true"
                  :true-value="true"
                >
                  Create a warning.
                </checkbox>
              </div>
              <div
                v-if="warningNOV"
                class="control is-expanded top"
              >
                <label for="">
                  <datepicker
                    v-model="graceDate"
                    :disabled-dates="disabledDates"
                    name="graceDate"
                    placeholder="Grace date"
                    input-class="input"
                  />
                </label>
              </div>
            </div>
          </div> -->
          <div class="field is-grouped is-grouped-right">
            <div class="control">
              <button
                class="button is-light"
                @click.prevent="updateInspection = false"
              >
                Cancel
              </button>
            </div>
            <div class="control">
              <button
                class="button update-button"
                :disabled="updatingInspection"
                :class="{ 'is-loading': updatingInspection }"
                @click="updateStatus"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import File from '@/components/InspectionForm/File';
import Uploader from '@/components/InspectionForm/Uploader';
import DropdownWithValidation from "@/components/common/DropdownWithValidation";
// import Checkbox from "@/components/common/Checkbox";
// import Datepicker from 'vuejs-datepicker';

const numberText = [ '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th' ];
export default {
  name: 'Inspection',
  components: {
    File,
    Uploader,
    DropdownWithValidation,
    // Checkbox,
    // Datepicker,
  },
  props: {
    isModal: {
      type: Boolean,
      required: false,
      default: false,
    },
    propertyType: {
      type: String,
      required: true,
    },
    inspection: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    eligibilityCheckId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      updateInspection: false,
      updatingInspection: false,
      certificateFile: null,
      showForm: false,
      uploading: false,
      cantCancel: false,
      certificateAwsFileName: '',
      status: null,
      statusReason: null,
      statusComment: null,

      warningNOV: false,
      graceDate: null,
      disabledDates: {
        to: new Date(),
      },

      showAllCertificates: false,

      // Reasons to change the status:
      reasons: {
        Failed: {
          'The dust wipes laboratory report shows that one or more dust wipes samples failed the EPA clearance levels limits.':
            'The dust wipes laboratory report shows that one or more dust wipes samples failed the EPA clearance levels limits.',
          'The lead safe certificate has expired.': 'The lead safe certificate has expired.',
          'EPA firm certification number was not provided.': 'EPA firm certification number was not provided.',
          'Lead free certification inspection report does not provide initial and/or ending calibration readings.':
            'Lead free certification inspection report does not provide initial and/or ending calibration readings.',
          'Lead free certification inspection report does not provide valid risk assessor credentials.':
            'Lead free certification inspection report does not provide valid risk assessor credentials.',
          'Lead free certification inspection report does not provide corresponding performance characteristic sheet.':
            'Lead free certification inspection report does not provide corresponding performance characteristic sheet.',
          'Lead free certification inspection report shows positive lead readings.':
            'Lead free certification inspection report shows positive lead readings.',
          
          'Other': 'Other',
        },
        Revoked: {
          'The lead safe or lead free certification provides false information.': 'The lead safe or lead free certification provides false information.',
          'The lab report/certificate is illegible': 'The lab report/certificate is illegible',
          'Lead safe certification submitted as a lead free certification': 'Lead safe certification submitted as a lead free certification',
          'Lead free certification submitted as a lead safe certification': 'Lead free certification submitted as a lead safe certification',
          'Other': 'Other',
        },
      },
    };  
  },
  computed: {
    statusOptions() {
      let statuses = { 'Passed': 'Passed', 'Failed': 'Failed', 'Revoked': 'Rejected' };
      delete statuses[this.inspection.status];
      return statuses;
    },
    uploadCertificateURL() {
      if (this.isAdmin) {
        return `admin/eligibility-check/${this.$route.params.uuid}/inspections/unit/files`;
      }
      return `eligibility-check/${this.$route.params.uuid}/inspections/unit/files`;
    },
    loggedIn() {
      return this.$store.state.auth.account;
    },
    inspectionStatusHistory: {
      get() {
        return this.$store.state.properties.inspectionStatusHistory[this.inspection.id] || [];
      },
      cache: false,
    },
    // inspectionCertificates() { // TODO: I was trying something here, when I have more time I will take care of this...
    //   if (!this.showAllCertificates) {
    //     return this.inspection.certificates.splice(0, 2);
    //   } else {
    //     return this.inspection.certificates;
    //   }
    // }
  },
  beforeMount() {
    let time = new Date();
    time.setDate(time.getDate()+30);
    this.graceDate = time;
  },
  mounted() {
    this.$nextTick(this.getInspectionStatusTimeline);
  },
  methods: {
    async getInspectionStatusTimeline() {
      await this.$store.dispatch('properties/getInspectionStatusTimeline', {
        eligibilityCheckId: this.eligibilityCheckId,
        inspectionId: this.inspection.id,
        inspectionName: this.inspection.name,
      });

      this.$forceUpdate();
    },
    editLink() {
      const params = { uuid: this.$route.params.uuid, unit: this.inspection.name };
      if (/Passed|Pending/gi.test(this.inspection.status) && this.isAdmin) {
        return { name:'update-inspections-one', params };
      }
      return { name:'submit-inspections-one', params };
    },
    async updateStatus() {
      const isValid = await this.$refs['update-status'].validate();
      if (!isValid) {
        return false;
      }

      if (this.warningNOV) {
        if (!await this.submitGuard('Please confirm that you want to create a notice of violation case with this status update.')) {
          return;
        }
      }

      this.updatingInspection = true;

      const payload = {
        eligibilityCheckUUID: this.$route.params.uuid,
        inspectionId: this.inspection.id,
        user: `${this.loggedIn.firstName} ${this.loggedIn.lastName}`,
        data: {
          status: this.status,
          statusReason: this.statusReason,
          statusComment: this.statusComment,
        },

        warningNOV: this.warningNOV,
        graceDate: this.graceDate,
      };

      const response = await this.$store.dispatch('properties/updateInspectionStatus', payload);

      if (response) {
        this.status = null;
        this.statusReason = null;
        this.statusComment = null;
        this.updateInspection = false;

        this.getInspectionStatusTimeline();
      }

      this.updatingInspection = false;
    },
    requestCertificateController(File) {
      return {
        CertificateFile: File,
        unit: this.inspection.name,
      };
    },
    responseCertificateController(response) {
      return response.data.certificateFileName;
    },
    removeParametersController(response) {
      return {
        fileName: response.reportFileName || response.certificateFileName,
      };
    },
    cancelUpload() {
      this.$refs['upload-certificate'].reset();
      this.showForm = false;
    },
    async submitFile() {
      const isValid = this.$refs['submit-certificate'].validate();
      if (!isValid) {
        return;
      }

      if (!this.uploading) {
        this.uploading = true;
        if (await this.$refs['upload-certificate'].upload(this.certificateFile)) {
          if (this.certificateAwsFileName.length) {
            // File is submitted, now update the certificate in our server
            this.cantCancel = true;
            const uploaded = await this.$store.dispatch('properties/submitCertificate', {
              eligibilityCheck: this.$route.params.uuid,
              inspection: this.inspection.id,
              certificateAwsFileName: this.certificateAwsFileName,
            });

            if (uploaded) {
              this.cancelUpload();
            } else {
              this.$fatalError();
            }
          } else {
            this.$fatalError();
          }
        }
        this.cantCancel = false;
        this.uploading = false;
      }
    },

    getFigureTitle(figure) {
      if (this.propertyType === 'Residential') {
        if (figure.roomNumber === 0) {
          switch(figure.type) {
          case 'window':
            return 'Common area window sill';
          case 'no-window':
            return 'Common area is a bathroom with NO window sill';
          case 'floor':
            return 'Common area floor';
          default:
            return '-';
          }
        } else {
          return (figure.type === 'window')
            ? `${numberText[figure.roomNumber - 1]} bedroom window sill (${figure.location})`
            : `${numberText[figure.roomNumber - 1]} bedroom floor (${figure.location})`;
        }
      } else {
        switch(figure.type) {
        case 'window':
          return `${numberText[figure.roomNumber]} common area window sill`;
        case 'no-window':
          return `${numberText[figure.roomNumber]} Common area is a bathroom with NO window sill`;
        case 'floor':
          return `${numberText[figure.roomNumber]} Common area floor`;
        default:
          return '-';
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.inspection {
  p strong {
    display: block;
  }
  .link-file {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    display: block;
    max-width: 450px;
  }
}
.status-update {
  position: relative;
  > span.tag.date {
    position: absolute;
    top: 5px;
    right: 5px;
  }
}
</style>
<style lang="scss">
.top .vdp-datepicker__calendar {
  position: absolute;
  top: -282px;
}
</style>